import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { DocumentDuplicateIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import CelebrationIcon from "images/designB/celebrationicon.png";
import { useData } from "data/data-provider";
import { BOOKING_DETAILS, bookingWhen, cash, funnelHref } from "@1-now/booking-core";
import { payByText } from "../BookingSteps/payByDate";
import { retriveBooking } from "api/booking/booking";
import { trackBookingStep } from "analytics/bookingEvents";

interface PaymentSuccessProps {
  amount?: string;
  bookingId?: string;
  date?: string;
  time?: string;
  paymentMethod?: string;
  lastFourDigits?: string;
  email?: string;
  onComplete?: () => void;
}

export default function PaymentSuccess({
  amount,
  bookingId,
  date,
  time,
  paymentMethod = "Visa",
  lastFourDigits,
  email,
  onComplete,
}: PaymentSuccessProps) {
  const navigate = useNavigate();
  const location = useLocation();
  // 1NOW-10711: set by the checkout when the trip amount was captured but the
  // security deposit was declined. Shown here so the notice survives the toast.
  const state = location.state as
    | { depositNotice?: string | null; booking?: any }
    | null;
  const depositNotice = state?.depositNotice;
  const [searchParams] = useSearchParams();
  /* 1NOW-11198: a pay-later reservation ("Reserve" on the Pay step) ends here
     too, as /payment-success?reserved=<uuid>: nothing was paid, so the page
     says it is reserved and what is due, and the countdown takes the renter on
     to that booking's page the same way. The uuid is in the address, not only
     in the router state, so a reload is still this reservation (the state does
     not survive one): the page reads the booking again by its uuid. A card
     payment has no `reserved`, and the page is as it was. */
  const reservedId = searchParams.get("reserved");
  const reserved = Boolean(reservedId);
  const handed =
    reserved && state?.booking?.unique_identifier === reservedId ? state!.booking : null;
  const [reread, setReread] = useState<any>(null);
  useEffect(() => {
    if (!reservedId || handed) return;
    let live = true;
    retriveBooking(reservedId).then((read: any) => {
      // The call is typed { data }, but answers with the booking itself (or { error }).
      if (live && read && !read.error && read.id != null) setReread(read);
    });
    return () => {
      live = false;
    };
  }, [reservedId, handed]);
  const [countdown, setCountdown] = useState(5);
  const [progress, setProgress] = useState(100);

  const { bookingdetails: inMemory, ourCompany } = useData();
  const bookingdetails = reserved
    ? handed ?? reread ?? { unique_identifier: reservedId }
    : inMemory;
  // 1NOW-11501: the end of the funnel: a reservation, or a card payment that went through.
  useEffect(() => {
    trackBookingStep("payment_success", { pay_mode: reserved ? "later" : "now", booking_id: bookingdetails?.id });
  }, []);
  const due = reserved ? payByText(bookingdetails) : "";

  /* Where "go to my booking" goes: the booking by its uuid, the way the
     booking page reads it (funnelHref). After a refresh there is no booking in
     memory, and this went to /booking-details?id=undefined; with nothing to
     show, it goes home. */
  const bookingHref = bookingdetails ? funnelHref(BOOKING_DETAILS, bookingdetails) : "/";
  /* The branch's own time, as the booking page shows it (bookingWhen): a plain
     browser-zone parse converted it to the renter's browser zone. */
  const pickUp = bookingWhen(bookingdetails?.pick_up_time, ourCompany);
  const dropOff = bookingWhen(bookingdetails?.drop_off_time, ourCompany);
  const paid = bookingdetails?.final_price_without_security;

  useEffect(() => {
    const startTime = Date.now();
    const duration = 5000; // 5 seconds

    // Update countdown and progress based on elapsed time
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, duration - elapsed);
      const remainingSeconds = Math.ceil(remaining / 1000);
      const progressPercent = (remaining / duration) * 100;

      setCountdown(remainingSeconds);
      setProgress(progressPercent);

      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 100); // Update every 100ms for smooth progress

    // Auto-redirect after exactly 5 seconds, replacing this page, so Back from
    // the booking page does not land here again: Meta's Purchase is a URL rule
    // on this page, counted each time it is opened (see leavePaymentSuccess).
    const timer = setTimeout(() => {
      navigate(bookingHref, { replace: true });
    }, duration);

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [bookingHref, navigate, reserved]);

  const handleCopyBookingId = () => {
    // The number shown beside the button. It copied the uuid — which is what
    // opens the booking, not what the label names.
    navigator.clipboard.writeText(String(bookingdetails?.id ?? ""));
    toast.success("Booking ID copied to clipboard");
  };

  const handleComplete = () => {
    navigate(bookingHref, { replace: true });
  };

  return (
    // The site's theme (dark or light), as the booking steps and the Reserved page draw it: no fixed colours.
    <div className="min-h-screen flex items-center justify-center bg-bg-color px-4 py-8 text-text-color">
      <div className="max-w-2xl w-full rounded-2xl border border-secondary-100 bg-card-color shadow-lg p-8 md:p-12">
        {/* Celebration Icon */}
        <div className="flex justify-center mb-6">
          <img src={CelebrationIcon} alt="Celebration" />
        </div>

        <div className="max-w-xs mx-auto">
          <h1 className="text-3xl font-bold text-center text-text-color mb-2">
            {reserved ? "Your car is reserved" : "Payment Successful"}
          </h1>
          {depositNotice && (
            <p
              role="status"
              className="text-sm text-center text-text-color bg-amber-500/10 border border-amber-500/40 rounded-lg px-3 py-2 mb-3"
            >
              {depositNotice}
            </p>
          )}

          <p className="text-center text-secondary-text-color mb-8 text-base ">
            {reserved
              ? `You'll pay later${due ? `: pay by ${due}` : ""}. The rental company will send you a payment link.`
              : "Thank you for your payment. Your transaction was processed successfully."}
          </p>
        </div>

        {/* Payment Details Box */}
        <div className="bg-secondary-100/40 rounded-xl p-6 mb-6 space-y-4">
          {/* Amount Paid */}
          <div className="flex justify-between items-center">
            <span className="text-secondary-text-color text-sm font-medium">
              {reserved ? "Amount Due" : "Amount Paid"}
            </span>
            <span className="text-2xl font-bold text-text-color">
              {paid != null && paid !== "" ? cash(paid) : "—"}
            </span>
          </div>

          {/* Booking ID */}
          <div className="flex justify-between items-center">
            <span className="text-secondary-text-color font-medium">Booking ID</span>
            <div className="flex items-center gap-2">
              <span className="text-text-color font-semibold">
                {bookingdetails?.id}
              </span>
              <button
                onClick={handleCopyBookingId}
                className="text-secondary-text-color hover:text-text-color transition-colors"
                title="Copy Booking ID"
              >
                <DocumentDuplicateIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Date & Time */}
          <div className="flex justify-between items-center">
            <span className="text-secondary-text-color font-medium">Pick-up</span>
            <span className="text-text-color font-semibold text-right">
              {pickUp ? `${pickUp.date} ${pickUp.time}` : "N/A"}
            </span>
          </div>

          {/* Drop-off Time */}
          <div className="flex justify-between items-center">
            <span className="text-secondary-text-color font-medium">Drop-off</span>
            <span className="text-text-color font-semibold text-right">
              {dropOff ? `${dropOff.date} ${dropOff.time}` : "N/A"}
            </span>
          </div>

        </div>

        {/* Complete Pending Steps Button */}
        <ButtonPrimary
          onClick={handleComplete}
          className="w-full text-on-primary  font-semibold py-4 rounded-xl flex items-center justify-center gap-2 transition-colors"
        >
          Complete Pending Steps
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 7l5 5m0 0l-5 5m5-5H6"
            />
          </svg>
        </ButtonPrimary>

        {/* Progress Bar */}
        <div className="mt-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-text-color">
              Redirecting to booking details...
            </span>
            <span className="text-sm text-text-color font-medium">
              {countdown}s
            </span>
          </div>
          <div className="w-full bg-secondary-100 rounded-full h-2 overflow-hidden">
            <div
              className="bg-primary-6000 h-2 rounded-full transition-all duration-100 ease-linear"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Footer Text */}
        <p className="text-center text-text-color text-sm mt-6">
          {/* Stage: the renter gets no reserved email here; the company sends the payment link from FMS. */}
          {reserved ? "Your payment link will be sent to" : "A confirmation email has been sent to"}{" "}
          <span className="text-text-color font-medium">
            {bookingdetails?.customer?.email}
          </span>
          .
        </p>
      </div>
    </div>
  );
}
